/* VAN MILE product page helpers: size guide dialog, sticky add to bag bar, sticky info column, lone last gallery photo,
   re-tapped option photo, "You may also like" pruning. Loaded by sections/main-product.liquid and
   sections/related-products.liquid. */
(() => {
  if (window.vmProductLoaded) return;
  window.vmProductLoaded = true;

  /* ------------------------------------------------------------ Size guide dialog */
  let openDialog = null;
  let openedWithHistory = false;
  let returnFocus = null;

  const fillDialog = (dialog) => {
    const body = dialog.querySelector('[data-vm-size-guide-body]');
    const template = dialog.querySelector('template[data-vm-size-guide-content]');
    if (body && template && !body.dataset.filled) {
      body.appendChild(template.content.cloneNode(true));
      body.dataset.filled = 'true';
    }
  };

  const closeDialog = (fromHistory = false) => {
    if (!openDialog) return;
    const dialog = openDialog;
    openDialog = null;
    if (dialog.open) dialog.close();
    document.documentElement.classList.remove('vm-dialog-open');
    if (openedWithHistory && !fromHistory) {
      openedWithHistory = false;
      try {
        if (history.state && history.state.vmSizeGuide) history.back();
      } catch (e) {
        /* history not available */
      }
    }
    openedWithHistory = false;
    if (returnFocus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true });
    returnFocus = null;
  };

  const showDialog = (dialog, trigger) => {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    fillDialog(dialog);
    returnFocus = trigger || document.activeElement;
    dialog.showModal();
    openDialog = dialog;
    document.documentElement.classList.add('vm-dialog-open');
    dialog.querySelector('[data-vm-size-guide-body]')?.scrollTo?.(0, 0);
    try {
      history.pushState({ ...(history.state || {}), vmSizeGuide: true }, '');
      openedWithHistory = true;
    } catch (e) {
      openedWithHistory = false;
    }
    return true;
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-vm-size-guide-open]');
    if (opener) {
      const id = opener.getAttribute('aria-controls');
      const dialog = (id && document.getElementById(id)) || document.querySelector('dialog.vm-size-guide');
      if (dialog && showDialog(dialog, opener)) event.preventDefault();
      return;
    }
    if (event.target.closest('[data-vm-size-guide-close]')) {
      closeDialog();
      return;
    }
    // click on the backdrop (outside the panel) closes
    if (openDialog && event.target === openDialog) closeDialog();
  });

  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target === openDialog) {
        event.preventDefault();
        closeDialog();
      }
    },
    true
  );

  window.addEventListener('popstate', () => {
    if (openDialog) closeDialog(true);
  });

  /* ------------------------------------------------------------ Sticky add to bag bar (< 990px) */
  if (!customElements.get('vm-sticky-atc')) {
    customElements.define(
      'vm-sticky-atc',
      class VmStickyAtc extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.sectionId;
          this.button = this.querySelector('[data-vm-sticky-button]');
          this.label = this.querySelector('[data-vm-sticky-label]');
          this.sizeEl = this.querySelector('[data-vm-sticky-size]');
          this.mql = window.matchMedia('(max-width: 989px)');
          this.state = { ctaVisible: true, ctaAbove: false, footerVisible: false, typing: false };

          this.onButton = () => {
            const main = this.mainButton;
            if (!main || main.disabled) return;
            main.click();
          };
          this.button?.addEventListener('click', this.onButton);

          this.onFocusIn = (event) => {
            const t = event.target;
            const typing =
              t &&
              (t.tagName === 'TEXTAREA' ||
                t.tagName === 'SELECT' ||
                (t.tagName === 'INPUT' && !['radio', 'checkbox', 'button', 'submit'].includes(t.type)));
            if (typing !== this.state.typing) {
              this.state.typing = typing;
              this.update();
            }
          };
          document.addEventListener('focusin', this.onFocusIn);

          this.onChange = (event) => {
            if (event.target.closest?.(`#variant-selects-${this.sectionId}`)) this.syncSize();
          };
          document.addEventListener('change', this.onChange);

          this.onMql = () => this.update();
          this.mql.addEventListener?.('change', this.onMql);

          if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (event?.data?.sectionId && event.data.sectionId !== this.sectionId) return;
              this.syncSize();
              this.syncButton();
            });
          }
          this.onSizeState = () => {
            this.syncSize();
            this.syncButton();
          };
          document.addEventListener('vm:size-state', this.onSizeState);

          this.observe();
          this.syncButton();
        }

        disconnectedCallback() {
          this.io?.disconnect();
          this.mo?.disconnect();
          this.unsubscribe?.();
          document.removeEventListener('focusin', this.onFocusIn);
          document.removeEventListener('change', this.onChange);
          document.removeEventListener('vm:size-state', this.onSizeState);
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
          this.mql?.removeEventListener?.('change', this.onMql);
          this.setVisible(false);
        }

        get mainButton() {
          return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        }

        observe() {
          const main = this.mainButton;
          if (!main || !('IntersectionObserver' in window)) return;
          this.footer = document.querySelector('.footer, footer, [id$="__footer"]');
          this.io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.target === main) {
                this.state.ctaVisible = entry.isIntersecting;
                this.state.ctaAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              } else {
                this.state.footerVisible = entry.isIntersecting;
              }
            });
            this.update();
          });
          this.io.observe(main);
          if (this.footer) this.io.observe(this.footer);

          // a jump past the button (fling, restored scroll, anchor link) never crosses it, so the observer stays silent
          this.onScroll = () => {
            if (this.state.ctaVisible || this.scrollQueued) return;
            this.scrollQueued = true;
            requestAnimationFrame(() => {
              this.scrollQueued = false;
              const button = this.mainButton;
              if (!button) return;
              const ctaAbove = button.getBoundingClientRect().top < 0;
              if (ctaAbove === this.state.ctaAbove) return;
              this.state.ctaAbove = ctaAbove;
              this.update();
            });
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });

          // mirror the main button (label, disabled, loading)
          this.mo = new MutationObserver(() => this.syncButton());
          this.mo.observe(main, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'], childList: true, subtree: true, characterData: true });
        }

        syncButton() {
          const main = this.mainButton;
          if (!main || !this.button) return;
          const text = main.querySelector('span')?.textContent.trim();
          if (text && this.label && this.label.textContent.trim() !== text) this.label.textContent = text;
          this.button.disabled = main.disabled;
          this.button.toggleAttribute('aria-busy', main.classList.contains('loading'));
          this.button.classList.toggle('is-select-size', main.classList.contains('is-select-size'));
        }

        syncSize() {
          if (!this.sizeEl) return;
          const group = document.getElementById(`variant-selects-${this.sectionId}`)?.querySelector('[data-vm-size]');
          if (!group) return;
          const checked = group.querySelector('input[type="radio"]:checked');
          this.sizeEl.textContent = checked ? checked.value : this.dataset.selectSizeLabel || '';
        }

        update() {
          const s = this.state;
          this.setVisible(this.mql.matches && !s.ctaVisible && s.ctaAbove && !s.footerVisible && !s.typing);
        }

        setVisible(visible) {
          if (visible === this.visible) return;
          this.visible = visible;
          this.classList.toggle('is-visible', visible);
          this.toggleAttribute('inert', !visible);
          const root = document.documentElement;
          root.classList.toggle('vm-sticky-atc-on', visible);
          if (visible) {
            root.style.setProperty('--vm-sticky-atc-h', `${this.offsetHeight}px`);
          } else {
            root.style.removeProperty('--vm-sticky-atc-h');
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------ Sticky info column (>= 750px)
     A column taller than the viewport sticks by its bottom edge instead of its top, so nothing is cut off.
     While it is stuck, the browser cannot scroll a keyboard-focused link or button at its top into view (the column
     moves with the page), so the column itself shifts until that element sits between the header and the window
     bottom (html scroll-padding, which also clears the mobile add to bag bar). It settles back when focus leaves the
     column or a wheel or finger scrolls the page. Focus from a click or tap never moves it. */
  const stickyMql = window.matchMedia('(min-width: 750px)');
  const stickyOffset = 32;
  const keyboardColumns = new Set();
  let pointerFocus = false;
  let pointerTarget = null;
  document.addEventListener(
    'pointerdown',
    (event) => {
      pointerFocus = true;
      pointerTarget = event.target;
    },
    true
  );
  document.addEventListener('keydown', () => (pointerFocus = false), true);

  const revealFocus = (el, room) => {
    const focused = document.activeElement;
    if (!el.contains(focused)) return;
    // a visually hidden radio (size, option) shows as its label
    const box = (focused.matches('input[type="radio"], input[type="checkbox"]') && focused.labels?.[0]) || focused;
    const rect = box.getBoundingClientRect();
    if (!rect.height) return;
    const pad = getComputedStyle(document.documentElement);
    const min = Math.max(16, parseFloat(pad.scrollPaddingTop) || 0);
    const max = window.innerHeight - Math.max(16, parseFloat(pad.scrollPaddingBottom) || 0);
    let shift = 0;
    if (rect.top < min) shift = min - rect.top;
    else if (rect.bottom > max) shift = max - rect.bottom;
    if (!shift) return;
    // between its normal place (bottom-stuck when tall) and just under the header
    const lowest = Math.min(stickyOffset, room, max - el.offsetHeight);
    const top = Math.max(lowest, Math.min(el.getBoundingClientRect().top + shift, Math.max(stickyOffset, min)));
    el.style.setProperty('--vm-sticky-top', `${Math.round(top)}px`);
    // near the end of the gallery the column cannot slide that far: scroll the page for the rest
    const after = box.getBoundingClientRect();
    if (after.top < min - 1) window.scrollBy(0, after.top - min);
    else if (after.bottom > max + 1) window.scrollBy(0, after.bottom - max);
  };

  const applySticky = (el) => {
    if (!stickyMql.matches) {
      keyboardColumns.delete(el);
      el.style.removeProperty('--vm-sticky-top');
      return;
    }
    const room = window.innerHeight - el.offsetHeight - stickyOffset;
    if (keyboardColumns.has(el)) {
      revealFocus(el, room);
      return;
    }
    el.style.setProperty('--vm-sticky-top', `${Math.min(stickyOffset, room)}px`);
  };

  const settleSticky = () => {
    keyboardColumns.forEach((el) => {
      keyboardColumns.delete(el);
      applySticky(el);
    });
  };
  window.addEventListener('wheel', settleSticky, { passive: true });
  window.addEventListener('touchmove', settleSticky, { passive: true });

  const stickyColumns = () => {
    const columns = document.querySelectorAll('.vm-pdp .product__info-container.product__column-sticky');
    if (!columns.length || !('ResizeObserver' in window)) return;
    const ro = new ResizeObserver((entries) => entries.forEach((entry) => applySticky(entry.target)));
    columns.forEach((el) => {
      ro.observe(el);
      applySticky(el);
      if (el.dataset.vmFocusWatched) return;
      el.dataset.vmFocusWatched = 'true';
      el.addEventListener('focusin', (event) => {
        if (pointerFocus || !stickyMql.matches || event.target.closest('dialog')) return;
        keyboardColumns.add(el);
        // now, before the browser's own focus scroll (so the page does not jump), and again after it
        applySticky(el);
        requestAnimationFrame(() => applySticky(el));
      });
      el.addEventListener('focusout', () => {
        if (!keyboardColumns.has(el)) return;
        requestAnimationFrame(() => {
          // focus moved within the column (or the variant picker re-rendered), or the window lost focus
          if (el.contains(document.activeElement) || !document.hasFocus()) return;
          // a click on plain text inside the shifted column moves focus out of it: leave the column under the cursor
          // (the next wheel or touch scroll settles it)
          if (pointerFocus && pointerTarget && el.contains(pointerTarget)) return;
          keyboardColumns.delete(el);
          applySticky(el);
        });
      });
    });
    window.addEventListener('resize', () => columns.forEach(applySticky), { passive: true });
  };

  /* ------------------------------------------------------------ Desktop gallery: an odd last photo spans both columns
     (vm-product.css, >= 990px). Its sizes (snippets/product-thumbnail.liquid) describes a half-width tile, so the
     browser would load a file half as wide as it is drawn: put the tile's real width first. Checked again when
     product-info.js or media-gallery.js reorder the photos, and on resize. */
  const loneTileMql = window.matchMedia('(min-width: 990px)');
  const loneTileSizes = (list) => {
    list.querySelectorAll('.product__media-item img[sizes]').forEach((img) => {
      if (img.dataset.vmSizes === undefined) img.dataset.vmSizes = img.getAttribute('sizes');
      const item = img.closest('.product__media-item');
      const lone = loneTileMql.matches && item.matches(':last-child:nth-child(odd):not(:only-child)');
      const width = lone ? Math.ceil(item.getBoundingClientRect().width) : 0;
      const sizes = width ? `(min-width: 990px) ${width}px, ${img.dataset.vmSizes}` : img.dataset.vmSizes;
      if (img.getAttribute('sizes') !== sizes) img.setAttribute('sizes', sizes);
    });
  };
  const galleryLists = new Set();
  let galleryQueued = false;
  window.addEventListener(
    'resize',
    () => {
      if (galleryQueued || !galleryLists.size) return;
      galleryQueued = true;
      requestAnimationFrame(() => {
        galleryQueued = false;
        galleryLists.forEach((list) => (list.isConnected ? loneTileSizes(list) : galleryLists.delete(list)));
      });
    },
    { passive: true }
  );

  const watchGallery = () => {
    document.querySelectorAll('.vm-pdp.product--columns .product__media-list').forEach((list) => {
      if (galleryLists.has(list)) return;
      galleryLists.add(list);
      loneTileSizes(list);
      new MutationObserver(() => loneTileSizes(list)).observe(list, { childList: true });
    });
  };

  /* ------------------------------------------------------------ "You may also like": one card per style */
  const pruneRelated = (root) => {
    const items = root.querySelectorAll('.vm-rec__item');
    if (!items.length) return;
    const exclude = root.dataset.vmExcludeGroup || '';
    // products already in 'Complete the set' on this page
    const excludeHandles = new Set((root.dataset.vmExcludeHandles || '').split(',').filter(Boolean));
    const limit = parseInt(root.dataset.vmLimit, 10) || 4;
    let shown = 0;
    items.forEach((item) => {
      const group = item.dataset.vmGroup;
      if ((exclude && group && group === exclude) || excludeHandles.has(item.dataset.vmHandle) || shown >= limit) {
        item.remove();
        return;
      }
      item.classList.remove('vm-related__item--extra');
      shown += 1;
    });
    if (!shown) root.closest('.vm-related-wrap')?.setAttribute('hidden', '');
  };

  const watchRelated = () => {
    document.querySelectorAll('product-recommendations.vm-related').forEach((root) => {
      if (root.dataset.vmWatched) return;
      root.dataset.vmWatched = 'true';
      pruneRelated(root);
      new MutationObserver(() => pruneRelated(root)).observe(root, { childList: true });
    });
  };

  /* ------------------------------------------------------------ Re-tapping the chosen option shows its photo
     When the variants have their own photos the gallery opens on the product's main photo
     (snippets/product-media-gallery.liquid) while the first variant is already checked, and a checked radio fires no
     change event: show that variant's photo here (gallery and zoom viewer, as product-info.js does on a change). On a
     new pick the click comes before the change, so the selected-variant JSON still holds the previous variant, whose
     value for the tapped option differs, and this skips. */
  document.addEventListener('click', (event) => {
    const input = event.target.closest?.('variant-selects input[type="radio"]');
    if (!input || !input.checked) return;
    const selects = input.closest('variant-selects');
    const json = selects?.querySelector('[data-selected-variant]');
    let variant = null;
    try {
      variant = JSON.parse(json?.textContent || 'null');
    } catch (e) {
      return;
    }
    const position = [...selects.querySelectorAll('.product-form__input')].indexOf(input.closest('.product-form__input'));
    if (!variant?.featured_media || position < 0 || variant.options?.[position] !== input.value) return;
    const info = input.closest('product-info');
    const mediaId = variant.featured_media.id;
    info?.querySelector('media-gallery')?.setActiveMedia?.(`${info.dataset.section}-${mediaId}`, true);
    const modalContent = document.querySelector(`#ProductModal-${info?.dataset.section} .product-media-modal__content`);
    const modalMedia = modalContent?.querySelector(`[data-media-id="${mediaId}"]`);
    if (modalMedia && modalContent.firstElementChild !== modalMedia) modalContent.prepend(modalMedia);
  });

  const init = () => {
    stickyColumns();
    watchGallery();
    watchRelated();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

})();
